/**
 * Centralized Environment Configuration
 * 
 * This module provides a single source of truth for all environment-specific
 * configurations to ensure consistent API URLs and settings across the application.
 */

export interface EnvironmentConfig {
  API_BASE_URL: string;
  STRIPE_PUBLISHABLE_KEY: string;
  NODE_ENV: 'development' | 'production' | 'staging';
  APP_NAME: string;
  VERSION: string;
}

class ConfigService {
  private config: EnvironmentConfig;

  constructor() {
    this.config = this.loadConfiguration();
  }

  private loadConfiguration(): EnvironmentConfig {
    // Determine environment
    const nodeEnv = process.env.NODE_ENV as 'development' | 'production' | 'staging' || 'development';
    
    // Base configuration
    const baseConfig = {
      APP_NAME: '12and22 Hosting Platform',
      VERSION: '1.0.0',
      NODE_ENV: nodeEnv,
    };

    // Environment-specific configurations
    switch (nodeEnv) {
      case 'production':
        return {
          ...baseConfig,
          API_BASE_URL: process.env.REACT_APP_API_URL || 'https://12and22.com',
          STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
        };
      
      case 'staging':
        return {
          ...baseConfig,
          API_BASE_URL: process.env.REACT_APP_API_URL || 'https://staging.12and22.com',
          STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_staging',
        };
      
      case 'development':
      default:
        // Force bypass mode for development
        const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        const developmentStripeKey = !stripeKey || stripeKey.startsWith('pk_live_') 
          ? 'pk_test_bypass_development_mode' 
          : stripeKey;
          
        return {
          ...baseConfig,
          API_BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8001',
          STRIPE_PUBLISHABLE_KEY: developmentStripeKey,
        };
    }
  }

  public getConfig(): EnvironmentConfig {
    return this.config;
  }

  public getApiUrl(endpoint: string = ''): string {
    const baseUrl = this.config.API_BASE_URL.endsWith('/') 
      ? this.config.API_BASE_URL.slice(0, -1) 
      : this.config.API_BASE_URL;
    
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Ensure all API calls go through /api prefix
    if (endpoint && !endpoint.startsWith('/api/') && !endpoint.startsWith('api/')) {
      return `${baseUrl}/api${cleanEndpoint}`;
    }
    
    return `${baseUrl}${cleanEndpoint}`;
  }

  public isDevelopment(): boolean {
    return this.config.NODE_ENV === 'development';
  }

  public isProduction(): boolean {
    return this.config.NODE_ENV === 'production';
  }

  public isStaging(): boolean {
    return this.config.NODE_ENV === 'staging';
  }
}

// Export singleton instance
export const configService = new ConfigService();
export const config = configService.getConfig();

// Convenience exports
export const API_BASE_URL = config.API_BASE_URL;
export const getApiUrl = (endpoint: string = '') => configService.getApiUrl(endpoint);